'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Mail, Lock, User } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import LegalFooter from '@/components/LegalFooter';
import { useAuth } from '@/contexts/AuthContext';
import OnboardingModal from '@/components/OnboardingModal';

type AuthMode = 'login' | 'register';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterFormData {
  display_name: string;
  email: string;
  password: string;
  confirm_password: string;
  terms: boolean;
}

export default function AuthClient() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const registerForm = useForm<RegisterFormData>({
    defaultValues: { display_name: '', email: '', password: '', confirm_password: '', terms: false },
  });

  const handleLoginSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password, data.remember);
      toast.success('Willkommen zurück!', { icon: '👋' });
      router.push('/');
      router.refresh();
    } catch (err: any) {
      toast.error('E-Mail oder Passwort falsch. Bitte prüfe deine Eingaben.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (data: RegisterFormData) => {
    if (data.password !== data.confirm_password) {
      registerForm.setError('confirm_password', { message: 'Passwörter stimmen nicht überein' });
      return;
    }
    setLoading(true);
    try {
      // Read referral code from sessionStorage (set by /join page)
      let referralCode: string | null = null;
      try {
        referralCode = sessionStorage.getItem('referral_code');
      } catch {}

      const result = await signUp(data.email, data.password, {
        fullName: data.display_name,
        referralCode: referralCode || undefined,
      });
      toast.success('Registrierung erfolgreich! Willkommen bei WINEWOOD!', { icon: '🎉' });
      setShowOnboarding(true);
    } catch (err: any) {
      toast.error(err?.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => {
          setShowOnboarding(false);
          router.push('/');
          router.refresh();
        }}
      />
      <div className="w-full max-w-mobile animate-slide-up">
        <div
          className="rounded-t-[28px] sm:rounded-app p-6 sm:p-8"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', borderBottom: 'none' }}
        >
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <Image
              src="/assets/images/logo_grun_fur_app-1787922846254.png"
              alt="WINEWOOD Adventure Logo"
              width={180}
              height={72}
              className="object-contain"
              priority
            />
          </div>

          {/* Mode toggle */}
          <div
            className="flex rounded-pill p-1 mb-6"
            style={{ background: 'var(--muted)' }}
          >
            {(['login', 'register'] as AuthMode[]).map((m) => (
              <button
                key={`auth-mode-${m}`}
                onClick={() => setMode(m)}
                className="flex-1 py-2 rounded-pill text-sm font-semibold transition-all duration-200"
                style={{
                  background: mode === m ? 'var(--card-elevated)' : 'transparent',
                  color: mode === m ? 'var(--card-foreground)' : 'var(--muted-foreground)',
                  boxShadow: mode === m ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                {m === 'login' ? 'Anmelden' : 'Registrieren'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <LoginForm
              form={loginForm}
              onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
              loading={loading}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          ) : (
            <RegisterForm
              form={registerForm}
              onSubmit={registerForm.handleSubmit(handleRegisterSubmit)}
              loading={loading}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              showConfirm={showConfirm}
              setShowConfirm={setShowConfirm}
            />
          )}

          {/* Back to discovery */}
          <div className="mt-5 text-center">
            <Link
              href="/"
              className="text-xs text-muted-foreground hover:text-secondary-foreground transition-colors duration-150"
            >
              Ohne Anmeldung entdecken →
            </Link>
          </div>
        </div>

        {/* Legal footer below auth card */}
        <div
          className="rounded-b-[28px] sm:rounded-app px-4 pb-2"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', borderTop: '1px solid var(--border)' }}
        >
          <LegalFooter />
        </div>
      </div>
    </>
  );
}

/* ─── Login Form ─── */
interface LoginFormProps {
  form: ReturnType<typeof useForm<LoginFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
}

function LoginForm({ form, onSubmit, loading, showPassword, setShowPassword }: LoginFormProps) {
  const { register, formState: { errors } } = form;

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {/* Email */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="login-email">
          E-Mail
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Mail size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="deine@email.de"
            className="w-full pl-10 pr-4 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.email ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('email', {
              required: 'E-Mail ist erforderlich',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ungültige E-Mail-Adresse' },
            })}
          />
        </div>
        {errors.email && (
          <p className="mt-1.5 text-xs text-error">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="login-password">
          Passwort
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Lock size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="w-full pl-10 pr-11 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('password', {
              required: 'Passwort ist erforderlich',
              minLength: { value: 6, message: 'Mindestens 6 Zeichen' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 transition-colors duration-150"
            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
          >
            {showPassword ? (
              <EyeOff size={16} color="var(--muted-foreground)" />
            ) : (
              <Eye size={16} color="var(--muted-foreground)" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1.5 text-xs text-error">{errors.password.message}</p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2.5">
        <input
          id="login-remember"
          type="checkbox"
          className="w-4 h-4 rounded accent-primary cursor-pointer"
          {...register('remember')}
        />
        <label htmlFor="login-remember" className="text-xs text-muted-foreground cursor-pointer select-none">
          Angemeldet bleiben
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-pill text-sm font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-70"
        style={{ background: 'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)', color: '#fff' }}
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : null}
        {loading ? 'Anmelden...' : 'Anmelden'}
      </button>
    </form>
  );
}

/* ─── Register Form ─── */
interface RegisterFormProps {
  form: ReturnType<typeof useForm<RegisterFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  showConfirm: boolean;
  setShowConfirm: (v: boolean) => void;
}

function RegisterForm({
  form,
  onSubmit,
  loading,
  showPassword,
  setShowPassword,
  showConfirm,
  setShowConfirm,
}: RegisterFormProps) {
  const { register, formState: { errors } } = form;

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {/* Display name */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="reg-name">
          Anzeigename
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <User size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="reg-name"
            type="text"
            autoComplete="name"
            placeholder="Dein Name"
            className="w-full pl-10 pr-4 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.display_name ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('display_name', {
              required: 'Name ist erforderlich',
              minLength: { value: 2, message: 'Mindestens 2 Zeichen' },
            })}
          />
        </div>
        {errors.display_name && (
          <p className="mt-1.5 text-xs text-error">{errors.display_name.message}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="reg-email">
          E-Mail
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Mail size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            placeholder="deine@email.de"
            className="w-full pl-10 pr-4 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.email ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('email', {
              required: 'E-Mail ist erforderlich',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ungültige E-Mail-Adresse' },
            })}
          />
        </div>
        {errors.email && (
          <p className="mt-1.5 text-xs text-error">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="reg-password">
          Passwort
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Lock size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Mindestens 8 Zeichen"
            className="w-full pl-10 pr-11 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('password', {
              required: 'Passwort ist erforderlich',
              minLength: { value: 8, message: 'Mindestens 8 Zeichen erforderlich' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 transition-colors duration-150"
            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
          >
            {showPassword ? <EyeOff size={16} color="var(--muted-foreground)" /> : <Eye size={16} color="var(--muted-foreground)" />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1.5 text-xs text-error">{errors.password.message}</p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          Mindestens 8 Zeichen, Groß- und Kleinbuchstaben empfohlen
        </p>
      </div>

      {/* Confirm password */}
      <div>
        <label className="block text-xs font-semibold text-secondary-foreground mb-1.5" htmlFor="reg-confirm">
          Passwort bestätigen
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <Lock size={15} color="var(--muted-foreground)" />
          </div>
          <input
            id="reg-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Passwort wiederholen"
            className="w-full pl-10 pr-11 py-3 rounded-app-sm text-sm transition-all duration-150"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.confirm_password ? 'var(--error)' : 'var(--border)'}`,
              color: 'var(--card-foreground)',
            }}
            {...register('confirm_password', {
              required: 'Bitte Passwort bestätigen',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 transition-colors duration-150"
            aria-label={showConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'}
          >
            {showConfirm ? <EyeOff size={16} color="var(--muted-foreground)" /> : <Eye size={16} color="var(--muted-foreground)" />}
          </button>
        </div>
        {errors.confirm_password && (
          <p className="mt-1.5 text-xs text-error">{errors.confirm_password.message}</p>
        )}
      </div>

      {/* Terms */}
      <div>
        <div className="flex items-start gap-2.5">
          <input
            id="reg-terms"
            type="checkbox"
            className="w-4 h-4 mt-0.5 rounded accent-primary cursor-pointer flex-shrink-0"
            {...register('terms', { required: 'Bitte AGB akzeptieren' })}
          />
          <label htmlFor="reg-terms" className="text-xs text-muted-foreground cursor-pointer select-none leading-relaxed">
            Ich akzeptiere die{' '}
            <a href="/agb" className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer">
              AGB
            </a>{' '}
            und{' '}
            <a href="/datenschutz" className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer">
              Datenschutzerklärung
            </a>
          </label>
        </div>
        {errors.terms && (
          <p className="mt-1.5 text-xs text-error">{errors.terms.message}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-pill text-sm font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-70"
        style={{ background: 'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)', color: '#fff' }}
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : null}
        {loading ? 'Registrierung läuft...' : 'Konto erstellen'}
      </button>
    </form>
  );
}